/**
 *
 * IndividualBody2
 *
 */
import React, {
  forwardRef,
  memo,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useAdvisor } from 'app/Provider/AdvisorProvider';
import GooglePlacesSearch from '../GooglePlacesSearch';

import Select from 'react-select';
import {
  UserRound,
  Wallet,
  BriefcaseBusiness,
  Star,
  Building,
  MapPin,
  UsersRound,
} from 'lucide-react';
import { media } from 'styles/media';
import sebi from './assets/sebi.svg';
interface Props {
  onsubmit: (item) => void;
}

const validationSchema = Yup.object().shape({
  assests_under_management: Yup.string().required(
    'assets under management are required',
  ),
  sebi_ria_number: Yup.string()
    .required('SEBI RIA number is required')
    .matches(/^[A-Z]{3}\d{9}$/, 'Invalid format.(Example:IAN638107615)'),
  registration_type: Yup.string().required('This Field is required.'),
  first_name: Yup.string()
    .matches(
      /^[A-Z][A-Za-z\s]*$/,
      'First name should start with an uppercase letter and can only contain letters and spaces',
    )
    .min(3, 'Name should have atleast 3 Characters')
    .max(35, 'Name cannot exceed 35 Characters')
    .required('First name is required'),
  last_name: Yup.string()
    .required('Last name is required')
    .matches(
      /^[A-Z][A-Za-z\s]*$/,
      'Lastname should start with an uppercase letter and can only contain letters and spaces',
    ),
  years_of_experience: Yup.string()
    .required('Years of experience is required')
    .matches(/^\d{1,2}$/, 'Three or more digits are not allowed'),
  company_name: Yup.string().when('isBusiness', {
    is: true,
    then: schema =>
      schema
        .matches(
          /^(?=[A-Za-z0-9@&])/,
          'company name should not start with hypens,spaces',
        )
        .required('This Field is required')
        .min(2, 'Business name must be at least 2 characters')
        .max(100, 'Business name cannot exceed 100 characters'),
    otherwise: schema => schema,
  }),
  office_address: Yup.string().when('isBusiness', {
    is: true,
    then: schema =>
      schema
        .min(10, 'Address must be at least 10 characters')
        .max(200, 'Address cannot exceed 200 characters')
        .matches(
          /^[A-Za-z0-9][a-zA-Z0-9\s,\-#&()]*\.?$/,
          'Address contains invalid characters',
        )
        .required('This Field is required'),
    otherwise: schema => schema,
  }),
  specialization_areas: Yup.array()
    .of(Yup.string())
    .min(1, 'Please select at least one specialization area')
    .required('This Field is required'),
});

export const ProfessionalAdvisoryDetails = memo(
  forwardRef((props: Props, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { t, i18n } = useTranslation();
    const { advisorData, setTrackSubmission, prevStep } = useAdvisor();
    const formikRef = useRef<any>(null);
    const [selected, setSelected] = useState('');
    const specializations = [
      { value: 'financial_planning', label: 'Financial Planning' },
      { value: 'retirement_planning', label: 'Retirement Planning' },
      { value: 'tax_planning', label: 'Tax Planning' },
      { value: 'investment_advisory', label: 'Investment Advisory' },
      { value: 'estate_planning', label: 'Estate Planning' },
      { value: 'insurance_planning', label: 'Insurance Planning' },
      { value: 'wealth_management', label: 'Wealth Management' },
      { value: 'mutual_funds_advisory', label: 'Mutual Funds Advisory' },
      { value: 'stock_market_advisory', label: 'Stock Market Advisory' },
      { value: 'risk_management', label: 'Risk Management' },
      { value: 'portfolio_management', label: 'Portfolio Management' },
      { value: 'debt_management', label: 'Debt Management' },
    ];
    const assetsUnderManagementOptions = [
      { value: 'less_than_1_cr', label: 'Less than 1 cr' },
      { value: '1_to_10_cr', label: '1-10 cr' },
      { value: '10_to_50_cr', label: '10-50 cr' },
      { value: '50_to_100_cr', label: '50-100 cr' },
      { value: '100_to_500_cr', label: '100-500 cr' },
      { value: '500_plus_cr', label: '500+ cr' },
    ];

    const customStyles = {
      // Control styling
      control: provided => ({
        ...provided,
        minWidth: 0,
        flexGrow: 1,
        border: 'none',
        boxShadow: 'none',
        fontSize: '1rem',
        borderRadius: '10px',
        backgroundColor: 'transparent',
        gap: '15px',
        alignItems: 'center',
        '&:hover': {
          border: 'none',
        },
        outline: 'none',
      }),

      // Container styling
      container: provided => ({
        ...provided,
        minWidth: 0,
        flex: '1 1 auto',
      }),

      // Input field styling
      input: base => ({
        ...base,
        margin: 0,
        padding: 0,
        width: '100%',
      }),

      placeholder: base => ({
        ...base,
        fontSize: '14px',
        fontWeight: 300,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),

      singleValue: base => ({
        ...base,
        fontSize: '14px',
        fontWeight: 300,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),

      valueContainer: provided => ({
        ...provided,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: '0px',
        maxHeight: '52px',
        flexWrap: 'nowrap',
      }),

      // Option styling with transitions
      option: (provided, state) => ({
        // Default color
        ...provided,
        backgroundColor: selected === state.data.value ? '#e4dfff' : 'white',
        color: '#333',
        padding: '8px 16px',
        transition: 'background-color 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#e4dfff',
        },
        borderRadius: '10px',
      }),

      // Dropdown menu styling
      menu: provided => ({
        ...provided,
        marginTop: '10px',
        borderRadius: '10px',
        width: 'calc(100% + 54px)',
        left: '-54px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }),

      // Multi-select value styling
      multiValue: (provided, state) => {
        const isOnlyValue =
          state.selectProps.value && state.selectProps.value.length === 1;

        return {
          ...provided,
          backgroundColor: '#F3F1FE',
          borderRadius: '5px',
          padding: '2px 8px',
          border: '1px dashed #E4DFFF',
          // When it's the only value, give it more room
          maxWidth: isOnlyValue ? '100%' : '100px',
          minWidth: '40px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          flexGrow: isOnlyValue ? 1 : 0,
          flexShrink: 1,
          margin: '0 2px',
        };
      },

      multiValueLabel: provided => ({
        ...provided,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis', // Add ellipsis when text overflows
      }),
    };
    useImperativeHandle(ref, () => ({
      submitForm: () => {
        formikRef.current?.handleSubmit();
      },
    }));
    return (
      <Div>
        <Formik
          innerRef={formikRef} // Assign the formik ref here
          initialValues={{
            assests_under_management:
              advisorData?.individualdata?.assest_under_management?.join(
                ', ',
              ) || '',
            sebi_ria_number: advisorData?.individualdata.sebiria_number || '',
            registration_type: advisorData?.registrationType || '',
            first_name: advisorData.profile.first_name || '',
            last_name: advisorData.profile.last_name || '',
            specialization_areas:
              advisorData?.individualdata?.specialization_areas || '',
            years_of_experience:
              advisorData?.individualdata?.years_of_experience || '',
            company_name: advisorData?.individualdata?.company_name || '',
            office_address: advisorData?.individualdata?.office_address || '',
            isBusiness: !!(
              advisorData?.individualdata?.company_name ||
              advisorData?.individualdata?.office_address
            ),
          }}
          validationSchema={validationSchema}
          onSubmit={values => {
            setTrackSubmission('personalDetails', true);
            // Ensure specialization_areas is a string, then split it
            // const specializationAreasArray = values.specialization_areas
            //   .split(',') // Split by comma
            //   .map(area => area.trim()); // Trim spaces around each area
            const AssestsArray = values.assests_under_management
              .split(',') // Split by comma
              .map(area => area.trim());
            props.onsubmit({
              ...values,

              assests_under_management: AssestsArray,
            });
          }}
        >
          {({
            handleChange,
            values,
            errors,
            touched,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Text>
                Hi there {advisorData.profile.first_name}! You're almost there,
                just few more questions before your account is set up.
              </Text>
              <div className="d-flex flex-column position-relative">
                <Cover
                  className="d-flex align-items-center gap-3 mt-4"
                  hasError={errors.sebi_ria_number && touched.sebi_ria_number}
                >
                  <img src={sebi} alt="check" />
                  <RadioText>I am SEBI RIA</RadioText>
                  <StyledInput
                    name="sebi_ria_number"
                    value={values.sebi_ria_number}
                    onChange={handleChange}
                    autoComplete="off"
                    onBlur={handleBlur}
                    placeholder="Enter SEBI RIA Number"
                  />
                </Cover>
                {errors.sebi_ria_number && touched.sebi_ria_number && (
                  <Error className="ms-3">{errors.sebi_ria_number}</Error>
                )}
              </div>
              <InputWrapper>
                <div className="d-flex flex-column position-relative">
                  <Cover
                    className="d-flex align-items-center gap-3"
                    hasError={errors.first_name && touched.first_name}
                  >
                    <UserRound strokeWidth={1.25} size={19} />
                    <StyledInput
                      name="first_name"
                      placeholder="First name"
                      onChange={handleChange}
                      value={values.first_name}
                      autoComplete="off"
                      onBlur={handleBlur}
                    />
                  </Cover>
                  {errors.first_name && touched.first_name && (
                    <Error className="ms-3">{errors.first_name}</Error>
                  )}
                </div>
                <div className="d-flex flex-column position-relative">
                  <Cover
                    className="d-flex align-items-center gap-3"
                    hasError={errors.last_name && touched.last_name}
                  >
                    <UserRound strokeWidth={1.25} size={19} />
                    <StyledInput
                      name="last_name"
                      placeholder="Last name"
                      onChange={handleChange}
                      value={values.last_name}
                      autoComplete="off"
                      onBlur={handleBlur}
                    />
                  </Cover>
                  {errors.last_name && touched.last_name && (
                    <Error className="ms-3">{errors.last_name}</Error>
                  )}
                </div>
              </InputWrapper>

              <div className="d-flex flex-column position-relative">
                <TopWrapper>
                  <Text>Register yourself as</Text>
                  <RadioWrapper>
                    <div className="d-flex align-items-center gap-3">
                      <Field
                        type="radio"
                        name="registration_type"
                        value="Individual"
                        as={CustomRadioInput}
                      />

                      <UserRound strokeWidth={1.25} size={19} />
                      <RadioText>Individual Advisor</RadioText>
                    </div>
                    <div className="d-flex  align-items-center gap-3">
                      <Field
                        type="radio"
                        name="registration_type"
                        value="Company"
                        as={CustomRadioInput}
                      />
                      <UsersRound strokeWidth={1.25} size={19} />
                      <RadioText>Advisory Firm/Company</RadioText>
                    </div>
                  </RadioWrapper>
                  {errors.registration_type && touched.registration_type && (
                    <Error className="ms-2" radio>
                      {errors.registration_type}
                    </Error>
                  )}
                </TopWrapper>
              </div>
              <InputWrapper>
                <div className="d-flex flex-column position-relative">
                  <Cover
                    className="d-flex align-items-center gap-3"
                    hasError={
                      errors.assests_under_management &&
                      touched.assests_under_management
                    }
                  >
                    <Wallet strokeWidth={1.25} size={19} />
                    <Select
                      options={assetsUnderManagementOptions}
                      styles={customStyles}
                      value={assetsUnderManagementOptions.find(
                        option =>
                          option.value === values.assests_under_management,
                      )}
                      onChange={selectedOption => {
                        if (selectedOption) {
                          setSelected(selectedOption.value);
                          console.log(selected);
                        }

                        setFieldValue(
                          'assests_under_management',
                          selectedOption ? selectedOption.value : '',
                        );
                      }}
                      placeholder="Assets Under Management"
                    />
                  </Cover>
                  {errors.assests_under_management &&
                    touched.assests_under_management && (
                      <Error className="ms-2">
                        {errors.assests_under_management}
                      </Error>
                    )}
                </div>
                <div className="d-flex flex-column position-relative">
                  <Cover
                    className="d-flex align-items-center gap-3 "
                    hasError={
                      errors.years_of_experience && touched.years_of_experience
                    }
                  >
                    <BriefcaseBusiness strokeWidth={1.25} size={19} />
                    <StyledInput
                      type="text"
                      id="years_of_experience"
                      name="years_of_experience"
                      value={values.years_of_experience}
                      onChange={handleChange}
                      autoComplete="off"
                      onBlur={handleBlur}
                      placeholder="Years of experience"
                    />
                  </Cover>
                  {errors.years_of_experience &&
                    touched.years_of_experience && (
                      <Error className="ms-2">
                        {errors.years_of_experience}
                      </Error>
                    )}
                </div>
              </InputWrapper>
              <InputWrapper>
                <div className="d-flex flex-column position-relative">
                  <Cover
                    className="d-flex align-items-center gap-3"
                    hasError={
                      errors.specialization_areas &&
                      touched.specialization_areas
                    }
                  >
                    <Star strokeWidth={1.25} size={19} />
                    <Select
                      options={specializations}
                      styles={customStyles}
                      value={specializations.filter(option => {
                        // Ensure values.specialization_areas is always treated as an array
                        const specializationAreas = Array.isArray(
                          values.specialization_areas,
                        )
                          ? values.specialization_areas
                          : values.specialization_areas
                          ? [values.specialization_areas]
                          : [];

                        return specializationAreas.includes(option.value);
                      })}
                      onChange={selectedOptions => {
                        const selectedValues = selectedOptions
                          ? selectedOptions.map(option => option.value)
                          : [];
                        setFieldValue('specialization_areas', selectedValues);
                      }}
                      placeholder="Choose specialization areas"
                      isMulti
                    />
                  </Cover>
                  {errors.specialization_areas &&
                    touched.specialization_areas && (
                      <Error className="ms-3">
                        {errors.specialization_areas}
                      </Error>
                    )}
                </div>
              </InputWrapper>
              <Wrapper className="d-flex align-items-center position-relative justify-content-start gap-3">
                <StyledCheckbox
                  type="checkbox"
                  name="isBusiness"
                  checked={values.isBusiness}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div>
                  <TermsText>
                    I am operating under a different business name
                  </TermsText>
                </div>
              </Wrapper>
              {values.isBusiness && (
                <>
                  <InputWrapper className="mt-3">
                    <div className="d-flex flex-column position-relative">
                      <Cover
                        className="d-flex align-items-center gap-3"
                        hasError={errors.company_name && touched.company_name}
                        business
                      >
                        <Building strokeWidth={1.25} size={19} />
                        <StyledInput
                          type="text"
                          id="company_name"
                          name="company_name"
                          value={values.company_name}
                          onChange={handleChange}
                          autoComplete="off"
                          onBlur={handleBlur}
                          placeholder="Enter your name or business name"
                        />
                      </Cover>
                      {errors.company_name && touched.company_name && (
                        <Error className="ms-3">{errors.company_name}</Error>
                      )}
                    </div>
                  </InputWrapper>
                  <InputWrapper>
                    <div className="d-flex flex-column position-relative">
                      <Cover
                        className="d-flex align-items-center gap-3"
                        hasError={
                          errors.office_address && touched.office_address
                        }
                        business
                      >
                        <MapPin strokeWidth={1.25} size={19} />
                        <GooglePlacesSearch
                          type="text"
                          id="office_address"
                          name="office_address"
                          value={values.office_address}
                          onChange={handleChange}
                          onAddressSelect={address =>
                            setFieldValue('office_address', address)
                          }
                          placeholder="Office Address"
                          onBlur={handleBlur}
                        />
                      </Cover>
                      {errors.office_address && touched.office_address && (
                        <Error className="ms-3">{errors.office_address}</Error>
                      )}
                    </div>
                  </InputWrapper>
                </>
              )}
              <div className="d-flex align-items-center justify-content-end my-3">
                <div className="d-sm-flex d-md-none">
                  <Back onClick={() => prevStep()} disabled={true}>
                    Back
                  </Back>
                </div>
                <div>
                  <Button type="submit">Continue</Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Div>
    );
  }),
);
export const InputSvg = styled.img`
  width: 19px;
  height: 19px;
  display: flex;
  align-self: center;
`;
const Wrapper = styled.div<{ terms: boolean }>`
  margin-top: ${prop => (prop.terms ? '50px' : '30px')};
  ${media.small} {
    margin-top: 20px;
  }
  ${media.medium} {
    margin-top: 30px;
  }
`;

const StyledCheckbox = styled.input`
  width: 16px; /* Adjust the size of the checkbox */
  height: 16px;
  border-radius: 5px; /* Makes it slightly rounded */
  cursor: pointer;
  appearance: none;
  border: 1px solid #e4dfff; /* Default border */
  background-color: #ffffff;
  position: relative;

  &:checked {
    background-color: white;
    border-color: #8276f4; /* Change border color when checked */
  }

  &:checked::after {
    content: '';
    width: 5px; /* Width of the checkmark lines */
    height: 10px; /* Height of the checkmark lines */
    border: solid #af8eff; /* Add border color */
    border-width: 0 2px 2px 0; /* Right and bottom borders */
    position: absolute;
    top: 45%; /* Adjust vertical position */
    left: 50%; /* Adjust horizontal position */
    transform: translate(-50%, -50%) rotate(45deg); /* Rotate to form the "V" shape */
    transform-origin: center;
  }
`;
const Back = styled.button`
  font-family: Nunito;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  border: none;
  color: #8276f4;
  background: transparent;
  letter-spacing: 0%;
  text-align: center;
  &:disabled {
    color: #c8ccd8;
    background: transparent !important;
    opacity: 0.7; /* Optional: reduce opacity when disabled */
    cursor: not-allowed; /* Changes cursor to indicate button is disabled */
  }
`;
const Button = styled.button`
  height: 52px;
  background-color: #8276f4;
  padding: 15px 30px;
  border-radius: 30px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  // margin-top: 50px;
  border: none;
  &:hover {
    background-color: #5c54ad;
  }
`;
const TermsText = styled.h6<{ line: boolean; login: boolean }>`
  font-family: Nunito;
  font-weight: 600;
  font-size: 14px;
  color: ${prop => (prop.login ? ' #8276F4' : 'initial')};
  line-height: 16.37px;
  letter-spacing: ${prop => (prop.login ? '1px' : '0px')};
  margin: 0;
  text-decoration: ${prop => (prop.line ? 'underline' : 'none')};
`;
const StyledInput = styled.input`
  padding: 0.5rem;
  font-size: 1rem;
  border: none;
  outline: none;
  flex: 1;
  background: transparent;
  width: 0; /* This forces the input to shrink below its min-content width */
  min-width: 0;
  &::placeholder {
    font-size: 14px;
    font-weight: 300;
    font-family: Nunito;
    letter-spacing: 0%;
    text-color: #838eae;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  gap: 10px;
  margin-top: 30px;
  & > div {
    flex: 1;
    min-width: 0; /* This prevents flex items from overflowing */
  }
  ${media.small} {
    flex-direction: column;
    gap: 30px;
  }
  & > div {
    width: 100%;
  }
`;
const Cover = styled.div<{ business: boolean }>`
  border: 2px solid
    ${props =>
      props.hasError ? '#FFC6BF' : props.business ? ' #E4E6EC' : ' #C6C0FA'};
  height: 52px;
  width: 100%;
  border-radius: 10px;
  background: #ffffff;
  padding-top: 16px;
  // padding-right: 20px;
  padding-bottom: 16px;
  padding-left: 20px;
`;

const Div = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  // overflow-y:auto;
  border: 'solid black';
  ${media.small} {
    height: auto;
  }
`;

const Error = styled.div<{ radio: boolean }>`
  color: #ff7b6a;
  font-weight: 400;
  font-size: 10px;
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: ${prop => (prop.radio ? '8px' : '4px')};
  white-space: normal;
  max-width: 100%;
  line-height: 1.2;
`;
const Text = styled.div`
  width: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 700;
  margin-top: 10px;
  ${media.small} {
    font-size: 14px;
    font-weight: 700;
  }
`;

export interface props {
  width?: string;
}
const RadioText = styled.div`
  font-family: Nunito;
  font-weight: 300;
  font-size: 14px;
  line-height: 19.1px;
  letter-spacing: 0%;
`;
const CustomRadioInput = styled.input`
  &[type='radio'] {
    display: inline-block;
    width: 21px;
    height: 21px;
    border: 1.5px solid #8276f4;
    border-radius: 50%;
    background-color: white;
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    appearance: none;
    outline: none;
  }
  ${media.medium} {
    width: 32px; /* Adjust the size of the checkbox */
    height: 32px;
  }
  &:checked {
    background-color: #8276f4;
    border-color: #8276f4;
  }
  &:checked::after {
    content: '';
    width: 5px; /* Width of the checkmark lines */
    height: 9.5px; /* Height of the checkmark lines */
    border: solid white;
    border-width: 0 1.5px 1.5px 0; /* Right and bottom borders */
    position: absolute;
    top: 45%;
    left: 31%;
    transform: translate(-50%, -50%) rotate(45deg); /* Rotate to form the "V" shape */
    transform-origin: bottom right;
  }
`;
const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 30px;
  ${media.medium} {
    flex-direction: row;
    gap: 60px;
    align-items: center;
    justify-content: center;
  }
`;
const RadioWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
  ${media.small} {
    flex-direction: column;
    gap: 20px;
  }
`;
